import {useEffect, useState} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {appLink} from "@rw/cars_routes";
import {carsTheme} from "@rw/themes";
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from "formik";
import * as Yup from "yup";

import {fetchCitiesByStateName} from "../../app/api/fetch_cities";
import {Alert, AlertType} from "../../app/components/alert/Alert";
import {useAlert} from "../../app/components/alert/useAlert";
import {Spinner} from "../../app/components/Spinner";
import {getStateNameBySlug, Voivodeship, voivodeshipNames} from "../../app/types/locationTypes";
import {getSellerSlug} from "../../offer/utils/get_seller_slug";
import {ProfileImageUpload} from "./ProfileImageUpload";
import {fetchSellerProfile, saveSellerProfile} from "./seller_profile_api";
import {emptySellerProfile, normalizeSellerProfile, SellerProfilePayload} from "./seller_profile_types";

type SellerProfileFormProps = {
    userId: number | null;
    displayName: string | null;
};

const validationSchema = Yup.object({
    display_name: Yup.string().trim().required("Podaj nazwę sprzedawcy / firmy").max(120, "Max 120 znaków"),
    phone: Yup.string()
        .trim()
        .matches(/^(\+?\d[\d\s-]{7,})$/, "Niepoprawny numer telefonu")
        .required("Telefon jest wymagany"),
    email_public: Yup.string().trim().email("Niepoprawny email").max(120),
    description: Yup.string().trim().max(2000, "Max 2000 znaków"),
    address_street: Yup.string().trim().max(200),
    address_city: Yup.string().trim().max(100),
    address_voivodeship: Yup.string().trim(),
    address_postal: Yup.string()
        .trim()
        .matches(/^(\d{2}-\d{3})?$/, "Format: 00-000")
});

export const SellerProfileForm = ({userId, displayName}: SellerProfileFormProps) => {
    const {alert, showAlert} = useAlert();
    const [initialValues, setInitialValues] = useState<SellerProfilePayload | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [apiReady, setApiReady] = useState(true);
    const [savedSlug, setSavedSlug] = useState<string | undefined>();
    const [cities, setCities] = useState<string[]>([]);
    const [isCityDisabled, setIsCityDisabled] = useState(true);

    const loadCities = async (slug: string) => {
        setIsCityDisabled(true);
        const stateName = getStateNameBySlug(slug);
        if (!stateName) {
            setCities([]);
            return;
        }
        const fetched = await fetchCitiesByStateName(stateName);
        setCities(fetched);
        setIsCityDisabled(false);
    };

    useEffect(() => {
        let cancelled = false;
        fetchSellerProfile()
            .then((profile) => {
                if (cancelled) {
                    return;
                }
                setApiReady(true);
                setLoadError(null);
                const normalized = normalizeSellerProfile(profile);
                if (!normalized.display_name && displayName) {
                    normalized.display_name = displayName;
                }
                setSavedSlug(normalized.slug);
                setInitialValues(normalized);
                if (normalized.address_voivodeship) {
                    void loadCities(normalized.address_voivodeship);
                }
            })
            .catch((err: any) => {
                if (cancelled) {
                    return;
                }
                const status = err?.status;
                if (status === 404) {
                    setApiReady(false);
                    setLoadError(null);
                    setInitialValues({
                        ...emptySellerProfile(),
                        display_name: displayName || ""
                    });
                    return;
                }
                setLoadError("Nie udało się wczytać profilu sprzedawcy.");
                setInitialValues({
                    ...emptySellerProfile(),
                    display_name: displayName || ""
                });
            });
        return () => {
            cancelled = true;
        };
    }, [displayName, userId]);

    const handleSubmit = async (values: SellerProfilePayload, helpers: FormikHelpers<SellerProfilePayload>) => {
        try {
            const saved = await saveSellerProfile(values);
            const normalized = normalizeSellerProfile(saved || values);
            setSavedSlug(normalized.slug);
            setInitialValues(normalized);
            helpers.resetForm({values: normalized});
            setApiReady(true);
            showAlert("Profil sprzedawcy zapisany.", AlertType.success, 4000);
        } catch (err: any) {
            const status = err?.status;
            if (status === 404) {
                setApiReady(false);
                showAlert(
                    "Backend jeszcze nie obsługuje zapisu profilu (brak endpointu). Dane są gotowe w panelu — do podpięcia po stronie WP.",
                    AlertType.danger,
                    8000
                );
            } else {
                showAlert("Nie udało się zapisać profilu sprzedawcy.", AlertType.danger, 5000);
            }
        } finally {
            helpers.setSubmitting(false);
        }
    };

    if (!initialValues) {
        return (
            <div css={loadingWrap}>
                <Spinner />
                <span>Ładowanie profilu…</span>
            </div>
        );
    }

    return (
        <>
            <Formik<SellerProfilePayload> initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({values, isSubmitting, setFieldValue, dirty, handleChange}) => {
                    const liveSlug = savedSlug || getSellerSlug(values.display_name);
                    const publicPath = liveSlug ? appLink.seller.detail({slug: liveSlug}) : null;

                    return (
                        <FormWrapper>
                            <h2 css={{margin: "0 0 0.5rem"}}>Profil sprzedawcy</h2>
                            <p css={lead}>Te dane trafią na publiczną stronę sprzedawcy. Lokalizacja auta w ogłoszeniu może być inna niż adres siedziby.</p>
                            {!apiReady ? (
                                <p css={backendNote}>
                                    Endpoint profilu nie jest jeszcze dostępny na API — formularz działa, zapis zadziała po wdrożeniu backendu.
                                </p>
                            ) : null}
                            {loadError ? <p css={errorText}>{loadError}</p> : null}

                            <StyledForm>
                                <SectionTitle>Dane podstawowe</SectionTitle>
                                <FieldBlock>
                                    <label htmlFor="display_name">Nazwa publiczna</label>
                                    <Field id="display_name" css={inputStyle} name="display_name" type="text" placeholder="np. Auto Top" />
                                    <ErrorMessage name="display_name" component="div" style={{color: "red"}} />
                                    {publicPath ? (
                                        <p css={slugHint}>
                                            Publiczny URL:{" "}
                                            <a href={publicPath} target="_blank" rel="noreferrer">
                                                {publicPath}
                                            </a>
                                        </p>
                                    ) : null}
                                </FieldBlock>

                                <FieldGrid>
                                    <FieldBlock>
                                        <label htmlFor="phone">Telefon kontaktowy</label>
                                        <Field id="phone" css={inputStyle} name="phone" type="tel" placeholder="np. 603 641 498" />
                                        <ErrorMessage name="phone" component="div" style={{color: "red"}} />
                                    </FieldBlock>
                                    <FieldBlock>
                                        <label htmlFor="email_public">Email publiczny (opcjonalnie)</label>
                                        <Field id="email_public" css={inputStyle} name="email_public" type="email" placeholder="kontakt@firma.pl" />
                                        <ErrorMessage name="email_public" component="div" style={{color: "red"}} />
                                    </FieldBlock>
                                </FieldGrid>

                                <FieldBlock>
                                    <label htmlFor="description">Opis firmy / o sprzedawcy</label>
                                    <Field
                                        id="description"
                                        css={textareaStyle}
                                        name="description"
                                        as="textarea"
                                        rows={5}
                                        placeholder="Krótki opis działalności…"
                                    />
                                    <ErrorMessage name="description" component="div" style={{color: "red"}} />
                                </FieldBlock>

                                <SectionTitle>Adres siedziby</SectionTitle>
                                <FieldBlock>
                                    <label htmlFor="address_street">Ulica i numer</label>
                                    <Field id="address_street" css={inputStyle} name="address_street" type="text" placeholder="ul. Przykładowa 12" />
                                    <ErrorMessage name="address_street" component="div" style={{color: "red"}} />
                                </FieldBlock>
                                <FieldGrid>
                                    <FieldBlock>
                                        <label htmlFor="address_voivodeship">Województwo</label>
                                        <Field
                                            as="select"
                                            id="address_voivodeship"
                                            css={inputStyle}
                                            name="address_voivodeship"
                                            onChange={async (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                handleChange(e);
                                                await loadCities(e.target.value);
                                                setFieldValue("address_city", "");
                                            }}
                                        >
                                            <option value="">Wybierz województwo</option>
                                            {Object.entries(Voivodeship).map(([key, value]) => (
                                                <option key={key} value={value}>
                                                    {voivodeshipNames[value]}
                                                </option>
                                            ))}
                                        </Field>
                                    </FieldBlock>
                                    <FieldBlock>
                                        <label htmlFor="address_city">Miasto</label>
                                        <Field
                                            as="select"
                                            id="address_city"
                                            css={inputStyle}
                                            name="address_city"
                                            disabled={isCityDisabled || cities.length === 0}
                                        >
                                            <option value="">Wybierz miasto</option>
                                            {cities.map((city) => (
                                                <option key={city} value={city}>
                                                    {city}
                                                </option>
                                            ))}
                                            {values.address_city && !cities.includes(values.address_city) ? (
                                                <option value={values.address_city}>{values.address_city}</option>
                                            ) : null}
                                        </Field>
                                    </FieldBlock>
                                    <FieldBlock>
                                        <label htmlFor="address_postal">Kod pocztowy</label>
                                        <Field id="address_postal" css={inputStyle} name="address_postal" type="text" placeholder="42-200" />
                                        <ErrorMessage name="address_postal" component="div" style={{color: "red"}} />
                                    </FieldBlock>
                                </FieldGrid>

                                <SectionTitle>Grafiki</SectionTitle>
                                <ProfileImageUpload
                                    label="Logo"
                                    hint="Kwadratowe logo firmy (min. ~200×200)."
                                    previewUrl={values.logo_url}
                                    imageKey={values.logo_key}
                                    aspect="logo"
                                    onChange={({url, key}) => {
                                        setFieldValue("logo_url", url);
                                        setFieldValue("logo_key", key);
                                    }}
                                />
                                <ProfileImageUpload
                                    label="Zdjęcie w tle"
                                    hint="Szerokie zdjęcie okładki strony sprzedawcy (~1600×600)."
                                    previewUrl={values.cover_url}
                                    imageKey={values.cover_key}
                                    aspect="cover"
                                    onChange={({url, key}) => {
                                        setFieldValue("cover_url", url);
                                        setFieldValue("cover_key", key);
                                    }}
                                />

                                <button type="submit" css={submitBtn} disabled={isSubmitting || !dirty}>
                                    {isSubmitting ? "Zapisywanie…" : "Zapisz profil"}
                                </button>
                            </StyledForm>
                        </FormWrapper>
                    );
                }}
            </Formik>
            {alert.message && <Alert type={alert.type} message={alert.message} />}
        </>
    );
};

const FormWrapper = styled.div`
    width: 100%;
    padding: 24px 0 40px;
    background: #fff;
`;

const StyledForm = styled(Form)`
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    max-width: 720px;
`;

const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
`;

const FieldBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    label {
        font-size: 14px;
        font-weight: 700;
    }
`;

const SectionTitle = styled.h3`
    margin: 1rem 0 0;
    font-size: 16px;
    font-weight: 700;
`;

const lead = css`
    margin: 0 0 1.25rem;
    max-width: 42rem;
    color: #66676c;
    font-size: 15px;
    line-height: 1.45;
`;

const backendNote = css`
    margin: 0 0 1rem;
    padding: 10px 12px;
    border: 1px solid #f0c36d;
    background: #fff8e8;
    border-radius: 6px;
    font-size: 14px;
    color: #5c4a16;
`;

const errorText = css`
    color: #c62828;
    margin: 0 0 1rem;
`;

const slugHint = css`
    margin: 0;
    font-size: 13px;
    color: #66676c;

    a {
        color: ${carsTheme.colors.brand_primary};
    }
`;

const inputStyle = css`
    border: 1px solid #a7a7ab;
    padding-block: 10px;
    border-radius: 8px;
    padding-left: 0.6rem;
    background: #ffffff;

    &:hover,
    &:focus,
    &:active,
    &:focus:active {
        border-color: ${carsTheme.colors.brand_primary};
        outline: none;
    }
`;

const textareaStyle = css`
    ${inputStyle};
    resize: vertical;
    min-height: 110px;
    font-family: inherit;
`;

const submitBtn = css`
    align-self: flex-start;
    margin-top: 0.5rem;
    padding: 12px 22px;
    border: none;
    border-radius: 4px;
    background: ${carsTheme.colors.brand_primary};
    color: #fff;
    font-weight: 700;
    font-size: 15px;
    cursor: pointer;

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const loadingWrap = css`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem 0;
    color: #66676c;
`;
